/** The small arithmetic every Insights chart shares: a linear scale and a polyline from a series with gaps. */

export interface Scale {
  (value: number): number
}

/** Maps `[min, max]` onto `[start, end]` (end may be less than start for a y axis). */
export function linear(min: number, max: number, start: number, end: number): Scale {
  const span = max - min || 1
  return (value) => start + ((value - min) / span) * (end - start)
}

/** A polyline's points for a series with nulls, broken where a month has nothing: each run of points is one segment. */
export function segmentsOf(series: readonly (number | null)[], x: (index: number) => number, y: Scale): string[] {
  const segments: string[] = []
  let current: string[] = []
  series.forEach((value, index) => {
    if (value === null) {
      if (current.length > 0) {
        segments.push(current.join(' '))
        current = []
      }
      return
    }
    current.push(`${round(x(index))},${round(y(value))}`)
  })
  if (current.length > 0) {
    segments.push(current.join(' '))
  }
  return segments
}

export const round = (value: number): number => Math.round(value * 10) / 10

/** A domain with a little room above and below the data, snapped to a step, so a flat line is not glued to an edge. */
export function domainOf(values: readonly (number | null)[], fallback: [number, number], step = 5): [number, number] {
  const present = values.filter((value): value is number => value !== null)
  if (present.length === 0) {
    return fallback
  }
  const min = Math.floor((Math.min(...present) - step / 2) / step) * step
  const max = Math.ceil((Math.max(...present) + step / 2) / step) * step
  return min === max ? [min - step, max + step] : [min, max]
}

/** Evenly spaced ticks across a domain, `count` of them, ends included. */
export function ticksOf([min, max]: [number, number], count = 4): number[] {
  return Array.from({ length: count + 1 }, (_, index) => round(min + ((max - min) * index) / count))
}
